/// <amd-module name="Wit/Common/WorkItemDialogs/Components/MoveToProjectDialog" />
import * as React from "react";
import { ITeamProject, ITfsProjectService } from "TFS/Services/TfsProjectService";
import { useObservable, useObservableArray } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { useComponentContext } from "VSS/Platform/Layout";
import { Dialog } from "VSSUI/Dialog";
import { Dropdown } from "VSSUI/Dropdown";
import { FormItem } from "VSSUI/FormItem";
import { IListBoxItem, ListBoxItemType } from "VSSUI/ListBox";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
import { IWorkItemFieldChange } from "Wit/Common/BulkUpdateUtils/BulkUpdateInterfaces";
import { getWorkItemTypes, performBulkUpdate } from "Wit/Common/BulkUpdateUtils/BulkUpdateUtils";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import { AreaPathDropdown } from "Wit/Common/WorkItemControls/AreaPathDropdown";
import { IterationDropdown } from "Wit/Common/WorkItemControls/IterationDropdown";
import { INodeTreeItem } from "Wit/Common/WorkItemControls/NodeTreeItemProvider";
import { convertNodesToListBoxItems, normalizePath } from "Wit/Common/WorkItemControls/NodeUtils";
import * as Resources from "Wit/Common/WorkItemDialogs/Resources";
import { IWorkItemNodeService } from "Wit/Common/WorkItemNodeService/IWorkItemNodeService";
import { IWorkItemTypesService } from "Wit/Common/WorkItemTypesService/IWorkItemTypesService";
import { HtmlEditor } from "Wit/HtmlEditor/Components/HtmlEditor";
import { INamedFieldDataDictionary } from "Wit/WorkItemDataManager/Contracts";
import { IWorkItem } from "Wit/WorkItemModel/OM/WorkItemInterfaces";

export interface IMoveToProjectDialogProps {
    onDismiss: () => void;
    onModifyItems?: (items: IWorkItem[]) => void;
    selectedItems: INamedFieldDataDictionary[];
}

export const MoveToProjectDialog: React.FunctionComponent<IMoveToProjectDialogProps> = ({ onModifyItems, onDismiss, selectedItems }) => {
    const { pageContext } = useComponentContext();
    const workItemTypeService = pageContext.getService<IWorkItemTypesService>("IWorkItemTypesService");
    const projectsService = pageContext.getService<ITfsProjectService>("ITfsProjectService");

    const [isValid, setIsValid] = React.useState(false);
    const [working, setWorking] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState<string | undefined>(undefined);
    const allProjects = React.useRef<ITeamProject[]>([]);

    // currently selected values
    const [historyNotes] = useObservable("");
    const [selectedProject, setSelectedProject] = React.useState<string | undefined>();
    const selectedType = React.useRef<string | undefined>();

    // dropdown selections
    const projectSelection = React.useRef(new DropdownSelection());
    const typeSelection = React.useRef(new DropdownSelection());

    // selected nodes
    const [selectedAreaPath] = useObservable<string>("");
    const [selectedIteration] = useObservable<string>("");

    // item providers
    const [projectsProvider] = useObservableArray([loadingItem]);
    const [typesProvider] = useObservableArray<IListBoxItem>([]);
    const [areaPathProvider] = useObservableArray<IListBoxItem<INodeTreeItem>>([]);
    const [iterationProvider] = useObservableArray<IListBoxItem<INodeTreeItem>>([]);

    const updateProviders = async (project: string) => {
        typesProvider.value = [loadingItem];
        const types = await workItemTypeService.getVisibleWorkItemTypes(project);
        typesProvider.value = getListBoxItemsFromWorkItemTypes(types);

        // this will always be the "keep current types" option
        typeSelection.current.select(0);
        selectedType.current = keepCurrentTypeKey;

        const nodeService = pageContext.getService<IWorkItemNodeService>("IWorkItemNodeService");
        areaPathProvider.value = [loadingItem];
        const areaPathNode = await nodeService.getAreaPathNodes(project);
        if (areaPathNode) {
            areaPathProvider.value = convertNodesToListBoxItems(areaPathNode);
            selectedAreaPath.value = normalizePath(areaPathNode.path);
        }

        iterationProvider.value = [loadingItem];
        const iterationNode = await nodeService.getIterationNodes(project);
        if (iterationNode) {
            iterationProvider.value = convertNodesToListBoxItems(iterationNode);
            selectedIteration.value = normalizePath(iterationNode.path);
        }

        updateIsValid();
    };

    const onClickSave = async () => {
        const targetProjectName = allProjects.current.find(p => p.id === selectedProject)?.name;
        if (!selectedProject || !selectedAreaPath.value || !selectedIteration.value || !selectedType.current || !targetProjectName) {
            return;
        }

        try {
            setWorking(true);
            const bulkMoveResult = await moveWorkItems(pageContext, selectedItems, {
                areaPath: selectedAreaPath.value,
                historyValue: historyNotes.value,
                iterationPath: selectedIteration.value,
                sourceProjects: allProjects.current,
                targetProjectId: selectedProject,
                targetProjectName,
                targetType: selectedType.current !== keepCurrentTypeKey ? selectedType.current : undefined
            });

            if (!bulkMoveResult.workItems || !bulkMoveResult.success) {
                setErrorMessage(bulkMoveResult.errorMessage);
                setWorking(false);
            } else {
                onModifyItems && onModifyItems(bulkMoveResult.workItems);
                onDismiss();
            }
        } catch (e) {
            setErrorMessage(e.message);
            setWorking(false);
        }
    };

    const updateIsValid = () => {
        const valid = !!selectedProject && !!selectedAreaPath.value && !!selectedIteration.value && !!selectedType.current;
        if (valid !== isValid) {
            setIsValid(valid);
        }
    };

    React.useEffect(() => {
        if (!!selectedProject) {
            updateProviders(selectedProject);
        }
    }, [selectedProject]);

    React.useEffect(() => {
        // load project options
        const loadProjects = async () => {
            const projects = await projectsService.getAllProjectsLoadedPromise();
            projectsProvider.value = getListBoxItemsFromProjects(projects);
            allProjects.current = projects;
        };

        loadProjects();
    }, []);

    return (
        <Dialog
            calloutContentClassName="work-item-dialog"
            defaultActiveElement={".bolt-dialog-focus-element-cancel-button"}
            footerButtonProps={[
                {
                    className: "bolt-dialog-focus-element-cancel-button",
                    text: Resources.Cancel,
                    onClick: onDismiss
                },
                {
                    text: Resources.OK,
                    disabled: !isValid || working,
                    onClick: onClickSave,
                    primary: true
                }
            ]}
            onDismiss={onDismiss}
            overlay={working ? { spinnerAriaLabel: Resources.MovingWorkItems } : undefined}
            resizable={true}
            showCloseButton={true}
            titleProps={{ text: Resources.MoveWorkItem }}
        >
            <div className="rhythm-vertical-16">
                {errorMessage && <MessageCard severity={MessageCardSeverity.Error}>{errorMessage}</MessageCard>}
                <FormItem label={Resources.ProjectDropdownLabel}>
                    <Dropdown
                        items={projectsProvider}
                        placeholder={Resources.ProjectDropdownPlaceholder}
                        selection={projectSelection.current}
                        onSelect={(e, item) => {
                            setSelectedProject(item.id);
                        }}
                    />
                </FormItem>
                <FormItem label={Resources.AreaPathLabel}>
                    <AreaPathDropdown
                        items={areaPathProvider}
                        onSetPath={path => {
                            selectedAreaPath.value = path;
                            updateIsValid();
                        }}
                        projectId={selectedProject}
                        selectedText={selectedAreaPath}
                    />
                </FormItem>
                <FormItem label={Resources.IterationPathLabel}>
                    <IterationDropdown
                        items={iterationProvider}
                        onSetPath={path => {
                            selectedIteration.value = path;
                            updateIsValid();
                        }}
                        projectId={selectedProject}
                        selectedText={selectedIteration}
                    />
                </FormItem>
                <FormItem label={Resources.WorkItemType}>
                    <Dropdown
                        items={typesProvider}
                        selection={typeSelection.current}
                        onSelect={(e, item) => {
                            selectedType.current = item.id === keepCurrentTypeKey ? item.id : item.text;
                            updateIsValid();
                        }}
                    />
                </FormItem>
                <FormItem label={Resources.MoveToProjectReasonLabel}>
                    <HtmlEditor
                        className="history-input"
                        placeholder={Resources.EditWorkItemsNotes}
                        htmlContent={historyNotes.value}
                        onChange={(newValue: string) => {
                            historyNotes.value = newValue;
                        }}
                        telemetryContext={{ controlName: "HtmlEditor" }}
                        showChromeBorder={true}
                    />
                </FormItem>
            </div>
        </Dialog>
    );
};

function getListBoxItemsFromProjects(options: ITeamProject[]): IListBoxItem[] {
    return options.map(option => {
        return { id: option.id, text: option.name } as IListBoxItem;
    });
}

const keepCurrentTypeKey = "keepCurrentType";
const keepCurrentTypeListBoxItem = { id: keepCurrentTypeKey, text: Resources.KeepCurrentTypesOption };

function getListBoxItemsFromWorkItemTypes(options: string[]): IListBoxItem[] {
    return [
        keepCurrentTypeListBoxItem,
        ...options.map(option => {
            return { id: option, text: option } as IListBoxItem;
        })
    ];
}

interface IBulkMoveResult {
    /**
     * Error message if the validation has failed
     */
    errorMessage?: string;
    /**
     * True if the bulk move was validated without any errors
     */
    success: boolean;
    /**
     * IWorkItem objects with updated project, type, and field data
     */
    workItems?: IWorkItem[];
}

interface IBulkMoveData {
    areaPath: string;
    historyValue: string;
    iterationPath: string;
    sourceProjects: ITeamProject[];
    targetProjectId: string;
    targetProjectName: string;
    targetType: string | undefined;
}

async function moveWorkItems(pageContext: IVssPageContext, items: INamedFieldDataDictionary[], update: IBulkMoveData): Promise<IBulkMoveResult> {
    const { sourceProjects, targetProjectId, targetProjectName, targetType } = update;
    const workItemTypesService = pageContext.getService<IWorkItemTypesService>("IWorkItemTypesService");
    const projectIdToTypeMap = getProjectIdToTypeMap(items, sourceProjects);

    // validate that all the work items can be moved from current project
    const sourceValidationResult = await validateSourceProjectWorkItemTypes(
        pageContext,
        projectIdToTypeMap,
        sourceProjects,
        /** keepExistingType */ !targetType
    );
    if (!sourceValidationResult.success) {
        return sourceValidationResult;
    }

    const workItemIds = items.map(i => Number(i[CoreFieldRefNames.Id]));
    const workItemTypes = getWorkItemTypes(items);
    const validTypeNames = await workItemTypesService.getVisibleWorkItemTypes(targetProjectId);
    const invalidTypeNames = (await workItemTypesService.getWorkItemTypes(targetProjectId)).filter(name => validTypeNames.indexOf(name) < 0);
    const targetWorkItemTypes = targetType ? [targetType] : workItemTypes;

    // validate that all work items can be moved to the new project
    const targetValidationResult = validateBulkMove(targetWorkItemTypes, validTypeNames, invalidTypeNames, targetProjectName, !targetType);
    if (!targetValidationResult.success) {
        return targetValidationResult;
    }

    const initialState = targetType ? await getInitialStateForTargetType(pageContext, targetProjectId, targetType) : undefined;
    const typesToFieldChanges = new Map<string, IWorkItemFieldChange[]>();
    for (const workItemType of workItemTypes) {
        typesToFieldChanges.set(workItemType, getFieldUpdatesForType(workItemType, update, initialState));
    }

    const workItems = await performBulkUpdate(pageContext, workItemIds, typesToFieldChanges);
    return {
        success: true,
        workItems
    };
}

function getFieldUpdatesForType(sourceType: string, update: IBulkMoveData, initialState: string | undefined = undefined) {
    const { areaPath, historyValue, iterationPath, targetProjectId, targetType } = update;
    const changes: IWorkItemFieldChange[] = [
        { fieldName: CoreFieldRefNames.WorkItemType, value: targetType ?? sourceType },
        { fieldName: CoreFieldRefNames.TeamProject, value: targetProjectId },
        { fieldName: CoreFieldRefNames.AreaPath, value: areaPath },
        { fieldName: CoreFieldRefNames.IterationPath, value: iterationPath }
    ];

    if (initialState) {
        changes.push({ fieldName: CoreFieldRefNames.State, value: initialState });
    }

    if (historyValue && historyValue.trim().length > 0) {
        changes.push({ fieldName: CoreFieldRefNames.History, value: historyValue });
    }

    return changes;
}

async function getInitialStateForTargetType(pageContext: IVssPageContext, targetProjectId: string, targetType: string) {
    const workItemTypeService = pageContext.getService<IWorkItemTypesService>("IWorkItemTypesService");
    const typeData = await workItemTypeService.getWorkItemTypeData(targetProjectId, [targetType]);

    // extract the initial state from the target work item type
    if (typeData.length === 1 && typeData[0].orderedStates && typeData[0].orderedStates.length > 0) {
        const targetTypeData = typeData[0];
        const initialState = targetTypeData.orderedStates![0];
        return initialState;
    }
}

/**
 * Validate a set of work item types against a set of valid and invalid types from the destination project
 */
function validateBulkMove(
    workItemTypes: string[],
    validWorkItemNames: string[],
    invalidWorkItemNames: string[],
    projectName: string,
    keepExistingTypeNames: boolean,
    isSourceProject = false
): IBulkMoveResult {
    const invalidTypeNames: string[] = [];
    let errorMessage = undefined;
    for (const typeName of workItemTypes) {
        const isHiddenType = invalidWorkItemNames.indexOf(typeName) >= 0;
        const isValidType = validWorkItemNames.indexOf(typeName) >= 0;

        // Hidden types can never move
        if (isHiddenType) {
            errorMessage = format(
                isSourceProject ? Resources.MoveSourceWorkItemBlockedCase : Resources.MoveTargetWorkItemBlockedCase,
                typeName,
                projectName
            );
        } else if (!isValidType) {
            invalidTypeNames.push(typeName);
        }
    }

    // Set error that type does not exist in target project
    if (invalidTypeNames.length > 0 && keepExistingTypeNames) {
        errorMessage = format(Resources.MoveWorkItemBulkTypeError, invalidTypeNames.join(", "), projectName);
    }

    return { errorMessage, success: !errorMessage };
}

/**
 * Return a mapping of projects -> unique types
 */
function getProjectIdToTypeMap(workItemData: INamedFieldDataDictionary[], sourceProjects: ITeamProject[]): Map<string, Set<string>> {
    const projectIdToTypeMap = new Map<string, Set<string>>();
    for (const workItem of workItemData) {
        const type = workItem[CoreFieldRefNames.WorkItemType];
        const projectName = workItem[CoreFieldRefNames.TeamProject];
        const project = sourceProjects.find(p => p.name === projectName);
        if (!project) {
            continue;
        }

        let typeSet = projectIdToTypeMap.get(project.id);
        if (!typeSet) {
            typeSet = new Set<string>();
            projectIdToTypeMap.set(project.id, typeSet);
        }

        typeSet.add(type);
    }

    return projectIdToTypeMap;
}

/**
 * Validate the work item types being moved are okay to move.
 * @param projectToTypeMapping  A mapping from project name -> work item type -> ids for each type
 * @param keepExistingTypes Whether or not types will be kept in the 'target' project or if there is a specific type they will all map to
 */
async function validateSourceProjectWorkItemTypes(
    pageContext: IVssPageContext,
    projectIdToTypeToIdsMap: Map<string, Set<string>>,
    sourceProjects: ITeamProject[],
    keepExistingTypes: boolean
): Promise<IBulkMoveResult> {
    const workItemTypesService = pageContext.getService<IWorkItemTypesService>("IWorkItemTypesService");
    for (const projectId of [...projectIdToTypeToIdsMap.keys()]) {
        const validTypeNames = await workItemTypesService.getVisibleWorkItemTypes(projectId);
        const invalidTypeNames = (await workItemTypesService.getWorkItemTypes(projectId)).filter(name => validTypeNames.indexOf(name) < 0);
        const types = projectIdToTypeToIdsMap.get(projectId);
        const projectName = sourceProjects.find(p => p.id === projectId)?.name;
        if (!types) {
            continue;
        }

        const result = validateBulkMove([...types.keys()], validTypeNames, invalidTypeNames, projectName ?? projectId, keepExistingTypes, true);
        if (!result.success) {
            return result;
        }
    }

    return { success: true };
}

const loadingItem: IListBoxItem = {
    id: "loading",
    type: ListBoxItemType.Loading
};
